/**
 * MD-11 FE-CHAT — SSE wire-format parser (text/event-stream → SseEvent[])
 *
 * Parses chunks of the EventSource wire format:
 *
 *   event: token
 *   id: 42
 *   data: {"type":"token","text":"..."}
 *
 *   event: citation_marker
 *   ...
 *
 * Returns parsed events + the last seen `id` (Last-Event-ID for reconnect).
 *
 * Day-1 implementation prefers `data:` JSON parse — server emits the type
 * inline within the JSON payload (matches MODULE-INTERFACES §1 SseEvent
 * discriminated union shape). The optional `event:` line is informational
 * only.
 *
 * Reference:
 *   - F-FE-CHAT.md §4.4 SSE consume
 *   - ARCH §4 SSE protocol
 */

import type { SseEvent } from './sse-types';

export type ParseResult = {
  events: SseEvent[];
  remainder: string; // bytes left over (incomplete frame)
  lastEventId?: string;
};

const SSE_FRAME_DELIMITER = '\n\n';

function safeJson(raw: string): unknown {
  try {
    return JSON.parse(raw);
  } catch {
    return null;
  }
}

function isSseEvent(value: unknown): value is SseEvent {
  if (!value || typeof value !== 'object') return false;
  const v = value as Record<string, unknown>;
  return typeof v.type === 'string';
}

/**
 * Parse one or more frames from `chunk`. Caller maintains rolling buffer:
 *
 *   const { events, remainder } = parseSseChunk(buffer + chunk);
 *   buffer = remainder;
 */
export function parseSseChunk(buffer: string): ParseResult {
  const events: SseEvent[] = [];
  let lastEventId: string | undefined;
  let cursor = 0;

  while (true) {
    const idx = buffer.indexOf(SSE_FRAME_DELIMITER, cursor);
    if (idx === -1) break;
    const frame = buffer.slice(cursor, idx);
    cursor = idx + SSE_FRAME_DELIMITER.length;

    let dataLines: string[] = [];
    for (const line of frame.split('\n')) {
      const trimmed = line.replace(/\r$/, '');
      if (trimmed.length === 0 || trimmed.startsWith(':')) continue;
      const colon = trimmed.indexOf(':');
      if (colon === -1) continue;
      const field = trimmed.slice(0, colon);
      const value = trimmed.slice(colon + 1).replace(/^\s/, '');
      if (field === 'data') dataLines.push(value);
      else if (field === 'id') lastEventId = value;
    }
    if (dataLines.length === 0) continue;

    const dataRaw = dataLines.join('\n');
    const parsed = safeJson(dataRaw);
    if (isSseEvent(parsed)) {
      events.push(parsed);
    }
  }

  return {
    events,
    remainder: buffer.slice(cursor),
    lastEventId,
  };
}
